// Data Import Tool Component
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  Database,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  AlertCircle,
  Loader2,
  Download,
  Link,
  Unlink,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Users,
  Briefcase,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider logos/icons
const providerConfig = {
  csv: { name: 'CSV File', icon: FileSpreadsheet, color: 'bg-emerald-100 text-emerald-700' },
  excel: { name: 'Excel File', icon: FileSpreadsheet, color: 'bg-green-100 text-green-700' },
  clio: { name: 'Clio', icon: Database, color: 'bg-blue-100 text-blue-700' },
  filevine: { name: 'Filevine', icon: Database, color: 'bg-purple-100 text-purple-700' },
  practicepanther: { name: 'PracticePanther', icon: Database, color: 'bg-amber-100 text-amber-700' },
  mycase: { name: 'MyCase', icon: Database, color: 'bg-pink-100 text-pink-700' }
};

const dataTypeConfig = {
  cases: { name: 'Cases/Matters', icon: Briefcase, color: 'text-blue-600' },
  contacts: { name: 'Contacts', icon: Users, color: 'text-emerald-600' },
  time_entries: { name: 'Time Entries', icon: Clock, color: 'text-purple-600' }
};

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle }
};

export default function DataImportTool() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState('cases');
  
  // File import state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [importSession, setImportSession] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [previewData, setPreviewData] = useState(null);
  
  // API connection state
  const [connections, setConnections] = useState([]);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [orgId, setOrgId] = useState('');
  
  // API Import options
  const [apiImportOptions, setApiImportOptions] = useState({
    matters: true,
    contacts: true,
    time_entries: false
  });
  
  // Import history
  const [importHistory, setImportHistory] = useState([]);
  
  // Loading states
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(false);

  useEffect(() => {
    fetchConnections();
    fetchImportHistory();
  }, [token]);

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/connections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setConnections(await res.json());
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setImportHistory(await res.json());
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  // Handle API import (Clio, Filevine)
  const handleApiImport = async () => {
    if (!selectedSource || selectedSource === 'file') return;
    
    setImporting(true);
    try {
      const params = new URLSearchParams({
        import_matters: apiImportOptions.matters,
        import_contacts: apiImportOptions.contacts,
        import_time_entries: apiImportOptions.time_entries
      });
      
      // For Filevine, use 'projects' instead of 'matters'
      if (selectedSource === 'filevine') {
        params.set('import_projects', apiImportOptions.matters);
      }
      
      const res = await fetch(`${API_URL}/api/import/import/${selectedSource}?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setImportSession({ import_id: data.import_id, status: 'processing', source: selectedSource });
        setStep(5);
        toast.success('Import started from ' + providerConfig[selectedSource]?.name);
        pollImportStatus(data.import_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start import');
      }
    } catch (error) {
      toast.error('Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadedFile(file);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data_type', selectedDataType);
    
    try {
      const res = await fetch(`${API_URL}/api/import/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setImportSession(data);
        setFieldMappings(data.suggested_mappings || {});
        setStep(3);
        toast.success('File uploaded successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload file');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async () => {
    if (!importSession?.import_id) return;
    
    setPreviewing(true);
    try {
      const res = await fetch(`${API_URL}/api/import/preview/${importSession.import_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fieldMappings)
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setStep(4);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to preview');
      }
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!importSession?.import_id) return;
    
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/import/execute/${importSession.import_id}?skip_errors=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fieldMappings)
      });
      
      if (res.ok) {
        toast.success('Import started');
        setStep(5);
        pollImportStatus(importSession.import_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start import');
      }
    } catch (error) {
      toast.error('Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const pollImportStatus = async (importId) => {
    setPollingStatus(true);
    
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/import/status/${importId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const status = await res.json();
          setImportSession(prev => ({ ...prev, ...status }));
          
          if (status.status === 'processing') {
            setTimeout(poll, 2000);
          } else {
            setPollingStatus(false);
            fetchImportHistory();
            if (status.status === 'completed') {
              toast.success(`Import completed! ${status.imported_count || 0} records imported.`);
            } else if (status.status === 'failed') {
              toast.error(`Import failed: ${status.error || 'Unknown error'}`);
            }
          }
        }
      } catch (error) {
        setPollingStatus(false);
      }
    };
    
    poll();
  };

  const handleConnectProvider = async (provider) => {
    if (!apiKey) {
      toast.error('Please enter your API key');
      return;
    }
    
    setConnectingProvider(provider);
    
    const formData = new FormData();
    formData.append('api_key', apiKey);
    if (provider === 'filevine' && orgId) {
      formData.append('org_id', orgId);
    }
    
    try {
      const res = await fetch(`${API_URL}/api/import/connect/${provider}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`Connected to ${providerConfig[provider]?.name || provider}`);
        setApiKey('');
        setOrgId('');
        fetchConnections();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to connect');
      }
    } catch (error) {
      toast.error('Failed to connect');
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleDisconnect = async (provider) => {
    try {
      const res = await fetch(`${API_URL}/api/import/connections/${provider}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Disconnected from ${providerConfig[provider]?.name || provider}`);
        fetchConnections();
      }
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedSource(null);
    setUploadedFile(null);
    setImportSession(null);
    setFieldMappings({});
    setPreviewData(null);
  };

  const isConnected = (provider) => connections.some(c => c.provider === provider);

  return (
    <div className="space-y-6">
      <Tabs defaultValue="import">
        <TabsList>
          <TabsTrigger value="import" data-testid="import-tab">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </TabsTrigger>
          <TabsTrigger value="connections" data-testid="connections-tab">
            <Link className="w-4 h-4 mr-2" />
            API Connections
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <History className="w-4 h-4 mr-2" />
            Import History
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: 'Source' },
              { num: 2, label: 'Data Type' },
              { num: 3, label: 'Map Fields' },
              { num: 4, label: 'Preview' },
              { num: 5, label: 'Import' }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step >= s.num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                `}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`ml-2 text-sm ${step >= s.num ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
                {i < 4 && <ArrowRight className="w-4 h-4 mx-4 text-muted-foreground" />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Source */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Import Source</CardTitle>
                <CardDescription>Choose where to import your data from</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {/* File Upload */}
                  <Card
                    className={`cursor-pointer transition-all hover:border-primary ${selectedSource === 'file' ? 'border-primary ring-2 ring-primary/20' : ''}`}
                    onClick={() => setSelectedSource('file')}
                    data-testid="source-file"
                  >
                    <CardContent className="p-6 text-center">
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-emerald-600" />
                      <h3 className="font-semibold mb-2">CSV / Excel File</h3>
                      <p className="text-sm text-muted-foreground">
                        Upload a file exported from any system
                      </p>
                    </CardContent>
                  </Card>

                  {/* API Integrations */}
                  {['clio', 'filevine', 'practicepanther', 'mycase'].map(provider => {
                    const config = providerConfig[provider];
                    const connected = isConnected(provider);
                    return (
                      <Card
                        key={provider}
                        className={`cursor-pointer transition-all hover:border-primary ${selectedSource === provider ? 'border-primary ring-2 ring-primary/20' : ''}`}
                        onClick={() => connected ? setSelectedSource(provider) : null}
                        data-testid={`source-${provider}`}
                      >
                        <CardContent className="p-6 text-center">
                          <config.icon className={`w-12 h-12 mx-auto mb-4 ${connected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <h3 className="font-semibold mb-2">{config.name}</h3>
                          {connected ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Connected
                            </Badge>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Connect in API tab first
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedSource}
                    data-testid="next-step-1"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Select Data Type / API Import Options */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedSource === 'file' ? 'What do you want to import?' : `Import from ${providerConfig[selectedSource]?.name}`}
                </CardTitle>
                <CardDescription>
                  {selectedSource === 'file' 
                    ? 'Select the type of data to import' 
                    : 'Select what data to import from your connected account'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedSource === 'file' ? (
                  <>
                    <div className="grid md:grid-cols-3 gap-4">
                      {Object.entries(dataTypeConfig).map(([type, config]) => (
                        <Card
                          key={type}
                          className={`cursor-pointer transition-all hover:border-primary ${selectedDataType === type ? 'border-primary ring-2 ring-primary/20' : ''}`}
                          onClick={() => setSelectedDataType(type)}
                          data-testid={`datatype-${type}`}
                        >
                          <CardContent className="p-6 text-center">
                            <config.icon className={`w-12 h-12 mx-auto mb-4 ${config.color}`} />
                            <h3 className="font-semibold">{config.name}</h3>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    <div className="flex justify-between mt-6">
                      <Button variant="outline" onClick={() => setStep(1)}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          data-testid="upload-file-btn"
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload File
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* API Import Options */}
                    <div className="space-y-4 max-w-md">
                      <div className="flex items-center space-x-3 p-4 border rounded-lg">
                        <Checkbox 
                          id="import-matters"
                          checked={apiImportOptions.matters}
                          onCheckedChange={(checked) => setApiImportOptions(prev => ({ ...prev, matters: checked }))}
                        />
                        <div className="flex-1">
                          <Label htmlFor="import-matters" className="text-base font-medium cursor-pointer">
                            <Briefcase className="w-4 h-4 inline mr-2 text-blue-600" />
                            {selectedSource === 'filevine' ? 'Projects' : 'Matters'} (Cases)
                          </Label>
                          <p className="text-sm text-muted-foreground">Import all cases/matters from {providerConfig[selectedSource]?.name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3 p-4 border rounded-lg">
                        <Checkbox 
                          id="import-contacts"
                          checked={apiImportOptions.contacts}
                          onCheckedChange={(checked) => setApiImportOptions(prev => ({ ...prev, contacts: checked }))}
                        />
                        <div className="flex-1">
                          <Label htmlFor="import-contacts" className="text-base font-medium cursor-pointer">
                            <Users className="w-4 h-4 inline mr-2 text-emerald-600" />
                            Contacts
                          </Label>
                          <p className="text-sm text-muted-foreground">Import all contacts from {providerConfig[selectedSource]?.name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3 p-4 border rounded-lg">
                        <Checkbox 
                          id="import-time"
                          checked={apiImportOptions.time_entries}
                          onCheckedChange={(checked) => setApiImportOptions(prev => ({ ...prev, time_entries: checked }))}
                        />
                        <div className="flex-1">
                          <Label htmlFor="import-time" className="text-base font-medium cursor-pointer">
                            <Clock className="w-4 h-4 inline mr-2 text-purple-600" />
                            Time Entries
                          </Label>
                          <p className="text-sm text-muted-foreground">Import time/billing entries (requires matters import)</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mt-6">
                      <Button variant="outline" onClick={() => setStep(1)}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <Button 
                        onClick={handleApiImport} 
                        disabled={importing || (!apiImportOptions.matters && !apiImportOptions.contacts)}
                        data-testid="start-api-import-btn"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Starting Import...
                          </>
                        ) : (
                          <>
                            <Database className="w-4 h-4 mr-2" />
                            Start Import
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 3: Field Mapping */}
          {step === 3 && importSession && (
            <Card>
              <CardHeader>
                <CardTitle>Map Your Fields</CardTitle>
                <CardDescription>
                  Match columns from your file to the system fields. 
                  {importSession.row_count} rows detected.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(importSession.target_fields || {}).map(([field, config]) => (
                    <div key={field} className="flex items-center gap-4">
                      <div className="w-48">
                        <Label className={config.required ? 'font-semibold' : ''}>
                          {config.label}
                          {config.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <Select
                        value={fieldMappings[field] || ''}
                        onValueChange={(v) => setFieldMappings(prev => ({ ...prev, [field]: v }))}
                      >
                        <SelectTrigger className="w-64" data-testid={`mapping-${field}`}>
                          <SelectValue placeholder="Select column..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">-- Skip --</SelectItem>
                          {importSession.columns?.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                {/* Preview Table */}
                {importSession.preview && importSession.preview.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-2">Sample Data (First 5 rows)</h4>
                    <div className="border rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {importSession.columns?.slice(0, 6).map(col => (
                              <TableHead key={col} className="whitespace-nowrap">{col}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importSession.preview.slice(0, 5).map((row, i) => (
                            <TableRow key={i}>
                              {importSession.columns?.slice(0, 6).map(col => (
                                <TableCell key={col} className="max-w-[200px] truncate">
                                  {row[col]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={handlePreview} disabled={previewing} data-testid="preview-btn">
                    {previewing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Preview...
                      </>
                    ) : (
                      <>
                        Preview Import
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Preview */}
          {step === 4 && previewData && (
            <Card>
              <CardHeader>
                <CardTitle>Review Import</CardTitle>
                <CardDescription>
                  {previewData.valid_rows} of {previewData.total_rows} rows will be imported
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{previewData.valid_rows}</div>
                    <div className="text-sm text-muted-foreground">Valid Rows</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{previewData.error_count}</div>
                    <div className="text-sm text-muted-foreground">Rows with Errors</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">{previewData.total_rows}</div>
                    <div className="text-sm text-muted-foreground">Total Rows</div>
                  </div>
                </div>

                {/* Errors */}
                {previewData.errors && previewData.errors.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Rows with Errors (will be skipped)
                    </h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-40 overflow-y-auto">
                      {previewData.errors.slice(0, 10).map((err, i) => (
                        <div key={i} className="text-sm text-red-700">
                          Row {err.row}: {err.errors?.join(', ') || err.error}
                        </div>
                      ))}
                      {previewData.errors.length > 10 && (
                        <div className="text-sm text-red-500 mt-2">
                          ... and {previewData.errors.length - 10} more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(previewData.preview[0] || {}).slice(0, 6).map(col => (
                          <TableHead key={col}>{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.preview.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {Object.entries(row).slice(0, 6).map(([key, val]) => (
                            <TableCell key={key} className="max-w-[200px] truncate">
                              {val || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={importing || previewData.valid_rows === 0}
                    data-testid="start-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Starting Import...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import {previewData.valid_rows} Records
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Import Progress */}
          {step === 5 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {importSession?.status === 'completed' ? 'Import Complete!' :
                   importSession?.status === 'failed' ? 'Import Failed' :
                   'Importing...'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center py-8">
                {importSession?.status === 'processing' && (
                  <>
                    <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
                    <p className="text-lg mb-4">Processing your data...</p>
                    {importSession?.imported_count > 0 && (
                      <p className="text-muted-foreground">
                        {importSession.imported_count} records imported so far
                      </p>
                    )}
                  </>
                )}
                
                {importSession?.status === 'completed' && (
                  <>
                    <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                    <p className="text-lg mb-2">Successfully imported {importSession.imported_count} records!</p>
                    {importSession.skipped_count > 0 && (
                      <p className="text-muted-foreground mb-4">
                        {importSession.skipped_count} rows skipped due to errors
                      </p>
                    )}
                    <Button onClick={resetWizard} className="mt-4">
                      Import More Data
                    </Button>
                  </>
                )}
                
                {importSession?.status === 'failed' && (
                  <>
                    <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                    <p className="text-lg mb-2">Import failed</p>
                    <p className="text-red-600 mb-4">{importSession.error}</p>
                    <Button onClick={resetWizard} variant="outline" className="mt-4">
                      Try Again
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* API Connections Tab */}
        <TabsContent value="connections" className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Connect Your Accounts</h3>
            <p className="text-muted-foreground">
              Connect to your existing case management software to import data directly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {['clio', 'filevine', 'practicepanther', 'mycase'].map(provider => {
              const config = providerConfig[provider];
              const connection = connections.find(c => c.provider === provider);
              
              return (
                <Card key={provider}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${config.color}`}>
                          <config.icon className="w-6 h-6" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{config.name}</CardTitle>
                          {connection && (
                            <p className="text-xs text-muted-foreground">
                              Connected {new Date(connection.connected_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {connection && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {connection ? (
                      <Button
                        variant="outline"
                        onClick={() => handleDisconnect(provider)}
                        className="w-full"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            value={connectingProvider === provider ? apiKey : ''}
                            onChange={(e) => {
                              setConnectingProvider(provider);
                              setApiKey(e.target.value);
                            }}
                            placeholder={`Enter ${config.name} API key`}
                            data-testid={`${provider}-api-key`}
                          />
                        </div>
                        {provider === 'filevine' && (
                          <div>
                            <Label>Organization ID</Label>
                            <Input
                              value={connectingProvider === provider ? orgId : ''}
                              onChange={(e) => {
                                setConnectingProvider(provider);
                                setOrgId(e.target.value);
                              }}
                              placeholder="Enter Org ID"
                            />
                          </div>
                        )}
                        <Button
                          onClick={() => handleConnectProvider(provider)}
                          disabled={connectingProvider === provider && !apiKey}
                          className="w-full"
                          data-testid={`connect-${provider}-btn`}
                        >
                          {connectingProvider === provider ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Link className="w-4 h-4 mr-2" />
                          )}
                          Connect
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Import History Tab */}
        <TabsContent value="history" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Import History</h3>
              <p className="text-muted-foreground">View your past imports</p>
            </div>
            <Button variant="outline" onClick={fetchImportHistory}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {importHistory.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Import History</h3>
                <p className="text-muted-foreground">
                  Your import history will appear here after you import data.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importHistory.map(imp => {
                    const StatusIcon = statusConfig[imp.status]?.icon || Clock;
                    return (
                      <TableRow key={imp.id}>
                        <TableCell>
                          {new Date(imp.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {imp.source || imp.filename || 'File'}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize">{imp.data_type}</TableCell>
                        <TableCell>
                          {imp.imported_count || 0} imported
                          {imp.skipped_count > 0 && `, ${imp.skipped_count} skipped`}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig[imp.status]?.color}>
                            <StatusIcon className={`w-3 h-3 mr-1 ${imp.status === 'processing' ? 'animate-spin' : ''}`} />
                            {statusConfig[imp.status]?.label || imp.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
